import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { utilityMeterService } from '../../services/api';

// 初始狀態
const initialState = {
  meters: {
    electricity: [],
    water: [],
    gas: []
  },
  selectedMeter: null,
  trends: {
    electricity: [],
    water: [],
    gas: []
  },
  loading: false,
  error: null
};

// 異步操作：獲取所有計量數據
export const fetchMeterData = createAsyncThunk(
  'utilityMeters/fetchMeterData',
  async (unitId, { rejectWithValue }) => {
    try {
      const response = await utilityMeterService.getAllMeterData(unitId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '獲取計量數據失敗');
    }
  }
);

// 異步操作：獲取電力計量數據
export const fetchElectricityData = createAsyncThunk(
  'utilityMeters/fetchElectricityData',
  async (unitId, { rejectWithValue }) => {
    try {
      const response = await utilityMeterService.getElectricityData(unitId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '獲取電力計量數據失敗');
    }
  }
);

// 異步操作：獲取水計量數據
export const fetchWaterData = createAsyncThunk(
  'utilityMeters/fetchWaterData',
  async (unitId, { rejectWithValue }) => {
    try {
      const response = await utilityMeterService.getWaterData(unitId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '獲取水計量數據失敗');
    }
  }
);

// 異步操作：獲取氣計量數據
export const fetchGasData = createAsyncThunk(
  'utilityMeters/fetchGasData',
  async (unitId, { rejectWithValue }) => {
    try {
      const response = await utilityMeterService.getGasData(unitId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '獲取氣計量數據失敗');
    }
  }
);

// 異步操作：獲取電力使用趨勢
export const fetchElectricityTrend = createAsyncThunk(
  'utilityMeters/fetchElectricityTrend',
  async ({ unitId, period }, { rejectWithValue }) => {
    try {
      const response = await utilityMeterService.getElectricityTrend(unitId, period);
      return { type: 'electricity', data: response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '獲取電力使用趨勢失敗');
    }
  }
);

// 異步操作：獲取水使用趨勢
export const fetchWaterTrend = createAsyncThunk(
  'utilityMeters/fetchWaterTrend',
  async ({ unitId, period }, { rejectWithValue }) => {
    try {
      const response = await utilityMeterService.getWaterTrend(unitId, period);
      return { type: 'water', data: response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '獲取水使用趨勢失敗');
    }
  }
);

// 異步操作：獲取氣使用趨勢
export const fetchGasTrend = createAsyncThunk(
  'utilityMeters/fetchGasTrend',
  async ({ unitId, period }, { rejectWithValue }) => {
    try {
      const response = await utilityMeterService.getGasTrend(unitId, period);
      return { type: 'gas', data: response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '獲取氣使用趨勢失敗');
    }
  }
);

// 異步操作：報告計量異常
export const reportMeterIssue = createAsyncThunk(
  'utilityMeters/reportMeterIssue',
  async (data, { rejectWithValue }) => {
    try {
      const response = await utilityMeterService.reportMeterIssue(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '報告計量異常失敗');
    }
  }
);

// 公用設施計量切片
const utilityMeterSlice = createSlice({
  name: 'utilityMeters',
  initialState,
  reducers: {
    selectMeter: (state, action) => {
      state.selectedMeter = action.payload;
    },
    clearSelectedMeter: (state) => {
      state.selectedMeter = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // 獲取所有計量數據
      .addCase(fetchMeterData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMeterData.fulfilled, (state, action) => {
        state.loading = false;
        state.meters = action.payload;
      })
      .addCase(fetchMeterData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 獲取電力計量數據
      .addCase(fetchElectricityData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchElectricityData.fulfilled, (state, action) => {
        state.loading = false;
        state.meters.electricity = action.payload;
      })
      .addCase(fetchElectricityData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 獲取水計量數據
      .addCase(fetchWaterData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWaterData.fulfilled, (state, action) => {
        state.loading = false;
        state.meters.water = action.payload;
      })
      .addCase(fetchWaterData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 獲取氣計量數據
      .addCase(fetchGasData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGasData.fulfilled, (state, action) => {
        state.loading = false;
        state.meters.gas = action.payload;
      })
      .addCase(fetchGasData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 獲取電力使用趨勢
      .addCase(fetchElectricityTrend.fulfilled, (state, action) => {
        state.trends.electricity = action.payload.data;
      })
      
      // 獲取水使用趨勢
      .addCase(fetchWaterTrend.fulfilled, (state, action) => {
        state.trends.water = action.payload.data;
      })
      
      // 獲取氣使用趨勢
      .addCase(fetchGasTrend.fulfilled, (state, action) => {
        state.trends.gas = action.payload.data;
      });
  }
});

export const { selectMeter, clearSelectedMeter } = utilityMeterSlice.actions;

export default utilityMeterSlice.reducer; 